import type { WidgetCell as Cell, WidgetDeclaration } from '@crowdex/plugin-contracts';

export type ValueFormat = { format: WidgetDeclaration['format']; currency: string | undefined };

/** Seconds as a duration reads: "42 s", "4 min 12", "1 h 05". */
export function formatDuration(seconds: number): string {
  const total = Math.round(Math.abs(seconds));
  const sign = seconds < 0 ? '−' : '';
  if (total < 60) return `${sign}${total} s`;
  const pad = (part: number) => String(part).padStart(2, '0');
  if (total < 3600) return `${sign}${Math.floor(total / 60)} min ${pad(total % 60)}`;
  return `${sign}${Math.floor(total / 3600)} h ${pad(Math.floor((total % 3600) / 60))}`;
}

/** A value as a stat or an axis shows it: compact (1.2K, $4.2M), in its unit. */
export function formatValue(value: number, { format, currency }: ValueFormat, compact = true) {
  if (format === 'duration') return formatDuration(value);
  const options: Intl.NumberFormatOptions = compact
    ? { notation: 'compact', maximumFractionDigits: Math.abs(value) < 10 ? 2 : 1 }
    : { maximumFractionDigits: 2 };
  if (format === 'percent')
    return new Intl.NumberFormat(undefined, { ...options, style: 'percent' }).format(value);
  if (format === 'currency' || currency !== undefined)
    return new Intl.NumberFormat(undefined, {
      ...options,
      style: 'currency',
      currency: currency ?? 'USD',
    }).format(value);
  return new Intl.NumberFormat(undefined, options).format(value);
}

/** A period's start as an axis label: `Oct 12`, or `Oct 2026` for months. */
export function formatPeriod(at: string, monthly: boolean): string {
  const date = new Date(at);
  return new Intl.DateTimeFormat(undefined, {
    timeZone: 'UTC',
    ...(monthly ? { month: 'short', year: 'numeric' } : { month: 'short', day: 'numeric' }),
  }).format(date);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;

/** A table's cell: dates and numbers readable, the rest as it is. */
export function formatCell(value: Cell, valueFormat: ValueFormat, column: string): string {
  if (value === null) return '—';
  if (typeof value === 'number')
    return column === 'value' ? formatValue(value, valueFormat, false) : value.toLocaleString();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (ISO_DATE.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime()))
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        ...(value.includes('T') ? { timeStyle: 'short' } : { timeZone: 'UTC' }),
      }).format(date);
  }
  return value;
}

/** A column's name as a header: its last part, in words (`user.login` → `Login`). */
export function columnTitle(column: string): string {
  const last = column.split('.').at(-1) ?? column;
  const words = last
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Readable since when: `just now`, `5 min ago`, `2 h ago`. */
export function formatAge(at: string, now = Date.now()): string {
  const minutes = Math.round((now - Date.parse(at)) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}
